/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseFinalReportPage.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/final-report 最終報告 + 結案ロック（S8 spec §8.2 /
 *                §11.2 畫面 13/14）。產生/呈現報告 snapshot、三方確認（EF / 億成 /
 *                朝日）、結案 → ClosedLocked;鎖定後唯讀凍結。
 *  Author      : EF Engineering
 *  Created     : 2026-06-01
 *  Updated     : 2026-06-01
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  資料來源（真相,執行中 binary 已驗 2026-06-01）：
 *    - workflow-state：權威狀態（驅動 review / close 推進的可達性與凍結判定）。
 *    - weight-audits：結案前置條件 —— 必須無「未解決」重量差異 review
 *      （resolution_type 為空者視為未解決,阻擋結案;對應後端 close 對未結
 *       AuditRequired 的守門）。
 *    - cases/:id：報告 snapshot 基本欄位（案番 / 標題 / 狀態）。
 *    - GET /cases/:id/final-report 為 v12-keyed,對 v13 案件回 404 → null（不阻擋）。
 *  spine 推進：
 *    - POST /cases/:id/final-report/review（FinalWeightReceived → FinalReportReview）。
 *    - POST /cases/:id/close（FinalReportReview → ClosedLocked）。
 *      ClosedLocked 後再 close → 後端回 409 CASE_STATE_TRANSITION_INVALID（凍結）。
 *  RBAC：review / close = marketplace.cases.update（PERMISSIONS.caseClose）。
 *  CLAUDE.md §2 invariant 5：ClosedLocked 後業務資料 100% 不可修改 → 鎖定後唯讀渲染。
 */

import { useMemo, type ReactElement } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { getCase } from '../api/cases'
import { listWeightVarianceReviews, type WeightVarianceReviewDto } from '../api/weights'
import { closeCase, getWorkflowState, openFinalReportReview } from '../api/workflow'
import { confirmFinalReport, getFinalReport, type FinalConfirmationParty } from '../api/reports'
import { ApiError } from '../api/client'
import { useStepUp } from '../components/StepUpDialog'
import { FreeeSlaPanel } from '../components/FreeeSlaPanel'
import { usePermissions } from '../hooks/usePermissions'
import { FINAL_CONFIRM_PERSONA_ROLES, PERMISSIONS } from '../lib/permissions'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Separator } from '../components/ui/separator'
import { Skeleton } from '../components/ui/skeleton'
import { toast } from '../components/ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

/** 結案目標狀態（workflow allowed_next 含此值時可結案）。 */
const CLOSED_STATE = 'ClosedLocked'
/** 開啟最終報告レビュー目標狀態。 */
const REVIEW_STATE = 'FinalReportReview'

function errDetail(error: unknown): string {
  return error instanceof ApiError ? `（${error.message}）` : ''
}

/** review 是否「未解決」（resolution_type 為空 → 阻擋結案）。 */
function isUnresolved(review: WeightVarianceReviewDto): boolean {
  return review.resolution_type === null || review.resolution_type === undefined
}

/** 三方確認 party（EF / 億成メンバー=member / 朝日会場=venue）+ confirm 按鈕 testid。 */
const CONFIRM_PARTIES: {
  party: FinalConfirmationParty
  labelKey: string
  testId: string
}[] = [
  { party: 'ef', labelKey: 'finalReport.role.ef', testId: 'confirm-ef' },
  { party: 'member', labelKey: 'finalReport.role.yicheng', testId: 'confirm-member' },
  { party: 'venue', labelKey: 'finalReport.role.asahi', testId: 'confirm-venue' },
]

export function CaseFinalReportPage({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  const queryClient = useQueryClient()
  const { can, roles } = usePermissions()
  const canClose = can(PERMISSIONS.caseClose)
  const canConfirm = can(PERMISSIONS.finalReportConfirm)

  const workflowQuery = useQuery({
    queryKey: ['case', caseId, 'workflow-state'],
    queryFn: () => getWorkflowState(caseId),
    enabled: caseId !== '',
  })
  const caseQuery = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCase(caseId),
    enabled: caseId !== '',
  })
  const reviewsQuery = useQuery({
    queryKey: ['case', caseId, 'weight-audits'],
    queryFn: () => listWeightVarianceReviews(caseId),
    enabled: caseId !== '',
  })
  // 最終報告 snapshot（v13 目前回 null;保留供日後 v13 補出 snapshot）。
  const finalReportQuery = useQuery({
    queryKey: ['case', caseId, 'final-report'],
    queryFn: () => getFinalReport(caseId),
    enabled: caseId !== '',
    retry: false,
  })

  const applyTransition = (workflowState: string) => {
    queryClient.setQueryData(['case', caseId, 'workflow-state'], (prev: unknown) =>
      prev && typeof prev === 'object' ? { ...prev, workflow_state: workflowState } : prev,
    )
    void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'workflow-state'] })
    void queryClient.invalidateQueries({ queryKey: ['case', caseId] })
  }

  const reviewMutation = useMutation({
    mutationFn: () => openFinalReportReview(caseId),
    onSuccess: (result) => {
      toast.success(t('finalReport.review.success'))
      applyTransition(result.workflow_state)
    },
    onError: (error) => toast.error(`${t('finalReport.review.error')}${errDetail(error)}`),
  })

  // 三方確認 — actor 自方を確認（後端が actor org と party の一致を検証）。
  const confirmMutation = useMutation({
    mutationFn: (party: FinalConfirmationParty) => confirmFinalReport(caseId, party),
    onSuccess: () => {
      toast.success(t('finalReport.confirm.success'))
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'final-report'] })
    },
    onError: (error) => toast.error(`${t('finalReport.confirm.error')}${errDetail(error)}`),
  })

  // 結案鎖定 = 高風險端點 → 送出前先 2FA step-up（spec §9.2）。
  const closeStepUp = useStepUp(t)
  const closeMutation = useMutation({
    mutationFn: (code: string) => closeCase(caseId, code),
    onSuccess: (result) => {
      toast.success(t('finalReport.close.success'))
      applyTransition(result.workflow_state)
    },
    onError: (error) => toast.error(`${t('finalReport.close.error')}${errDetail(error)}`),
  })

  const reviews = useMemo<WeightVarianceReviewDto[]>(
    () => reviewsQuery.data?.data ?? [],
    [reviewsQuery.data],
  )
  const unresolved = reviews.filter(isUnresolved)

  if (workflowQuery.isPending || caseQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }
  if (caseQuery.isError || !caseQuery.data) {
    return (
      <p className="text-sm text-destructive">
        {t('case.detail.loadError')}
        {errDetail(caseQuery.error)}
      </p>
    )
  }

  const detail = caseQuery.data
  const workflowState = workflowQuery.data?.workflow_state ?? detail.workflow_state ?? null
  const allowedNext = workflowQuery.data?.allowed_next ?? []
  const isClosed = workflowState === CLOSED_STATE
  const isLocked =
    isClosed || (workflowQuery.data?.is_blocking === true && allowedNext.length === 0)
  const canAdvanceReview = allowedNext.includes(REVIEW_STATE)
  const canAdvanceClose = allowedNext.includes(CLOSED_STATE)
  const closeBlockedByVariance = unresolved.length > 0
  const report = finalReportQuery.data ?? null
  const hasSnapshot = report != null
  // 確認済み party 集合（v13 read model の confirmations[]）。結案後（凍結）は全 party を確認済み扱い。
  // （早期 return 後の単純な集合構築のため hook 不要 ── react-hooks/rules-of-hooks 回避）。
  const confirmedParties: Set<FinalConfirmationParty> = isClosed
    ? new Set<FinalConfirmationParty>(['ef', 'member', 'venue'])
    : new Set((report?.confirmations ?? []).map((c) => c.party))
  const allConfirmed = confirmedParties.size === CONFIRM_PARTIES.length
  const closeBlockedByConfirm = !allConfirmed

  return (
    <div className="space-y-4">
      <header className="page-heading">
        <span className="eyebrow">{t('case.stage.finalReport')}</span>
        <h2 className="text-lg font-semibold">{t('finalReport.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('finalReport.subtitle')}</p>
      </header>

      {/* 結案狀態橫幅 */}
      {isLocked ? (
        <Card className="border-emerald-300 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Badge variant="outline">{t('finalReport.locked.badge')}</Badge>
              {t('finalReport.locked.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{t('finalReport.locked.body')}</p>
          </CardContent>
        </Card>
      ) : null}

      {/* 報告 snapshot */}
      <Card>
        <CardHeader>
          <CardTitle>{t('finalReport.snapshot.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <SnapshotStat label={t('field.caseNo')} value={detail.case_no} />
            <SnapshotStat label={t('finalReport.snapshot.subject')} value={detail.title} />
            <SnapshotStat label={t('finalReport.snapshot.state')} value={workflowState ?? '—'} />
          </dl>
          {hasSnapshot ? (
            <div className="rounded-md border border-border p-3">
              <p className="text-xs text-muted-foreground">{t('finalReport.snapshot.hash')}</p>
              <p
                data-testid="final-report-hash"
                className="mt-1 break-all font-mono text-xs"
                title={report.hash}
              >
                {report.hash}
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{t('finalReport.snapshot.none')}</p>
          )}
        </CardContent>
      </Card>

      {/* 三方確認 */}
      <Card>
        <CardHeader>
          <CardTitle>{t('finalReport.confirm.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">{t('finalReport.confirm.hint')}</p>
          <p
            data-testid="confirmations-status"
            className={
              allConfirmed
                ? 'text-sm font-medium text-emerald-600 dark:text-emerald-400'
                : 'text-sm text-muted-foreground'
            }
          >
            {allConfirmed
              ? t('finalReport.confirm.allConfirmed')
              : t('finalReport.confirm.progress')
                  .replace('{n}', String(confirmedParties.size))
                  .replace('{total}', String(CONFIRM_PARTIES.length))}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {CONFIRM_PARTIES.map((p) => {
              const confirmed = confirmedParties.has(p.party)
              // persona ゲート:actor が当該 party の代表 role を持ち、且つ confirm 権限あり、
              // snapshot 採取済み（review 開始後）かつ未確認かつ未凍結のときのみ確認可。
              const isPersona = roles.some((r) => FINAL_CONFIRM_PERSONA_ROLES[p.party].includes(r))
              const canConfirmThis =
                canConfirm && isPersona && hasSnapshot && !confirmed && !isLocked
              return (
                <div key={p.party} className="space-y-2 rounded-md border border-border p-3">
                  <p className="text-sm font-medium">{t(p.labelKey)}</p>
                  <Badge variant={confirmed ? 'secondary' : 'outline'}>
                    {confirmed
                      ? t('finalReport.confirm.confirmed')
                      : t('finalReport.confirm.pending')}
                  </Badge>
                  {canConfirmThis ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid={p.testId}
                      disabled={confirmMutation.isPending}
                      onClick={() => confirmMutation.mutate(p.party)}
                    >
                      {t('finalReport.confirm.button')}
                    </Button>
                  ) : null}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* 結案前置條件：重量差異 review 全數解決 */}
      <Card>
        <CardHeader>
          <CardTitle>{t('finalReport.precondition.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {reviewsQuery.isPending ? (
            <Skeleton className="h-16 w-full" />
          ) : reviews.length === 0 ? (
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              {t('finalReport.precondition.none')}
            </p>
          ) : (
            <>
              <p
                className={
                  closeBlockedByVariance
                    ? 'text-sm font-medium text-destructive'
                    : 'text-sm text-emerald-600 dark:text-emerald-400'
                }
              >
                {closeBlockedByVariance
                  ? t('finalReport.precondition.blocked').replace(
                      '{n}',
                      String(unresolved.length),
                    )
                  : t('finalReport.precondition.allResolved')}
              </p>
              <div className="overflow-x-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('weightAudit.col.varianceType')}</TableHead>
                      <TableHead className="text-right">{t('weightAudit.col.variance')}</TableHead>
                      <TableHead>{t('weightAudit.col.status')}</TableHead>
                      <TableHead>{t('weightAudit.col.resolution')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviews.map((review) => (
                      <TableRow key={review.id}>
                        <TableCell>{review.variance_type}</TableCell>
                        <TableCell className="text-right">{review.variance_kg}</TableCell>
                        <TableCell>
                          <Badge variant={isUnresolved(review) ? 'destructive' : 'secondary'}>
                            {review.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{review.resolution_type ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Freee SLA（計時 + 提醒事件，不串接 Freee API）+ 財務書類參照（spec §10.3 / §B4） */}
      <FreeeSlaPanel t={t} caseId={caseId} />

      {/* 推進動作（review / close）—— 鎖定後不顯示任何寫入入口 */}
      {!isLocked ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('finalReport.actions.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!canClose ? (
              <Badge variant="outline">{t('rbac.forbidden')}</Badge>
            ) : (
              <>
                {/* 開啟最終報告レビュー */}
                <div className="space-y-2">
                  <p className="text-sm font-medium">{t('finalReport.review.label')}</p>
                  {canAdvanceReview ? (
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="final-report-review"
                      disabled={reviewMutation.isPending}
                      onClick={() => reviewMutation.mutate()}
                    >
                      {reviewMutation.isPending
                        ? t('finalReport.review.running')
                        : t('finalReport.review.button')}
                    </Button>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('finalReport.review.notReady')}
                    </p>
                  )}
                </div>

                <Separator />

                {/* 結案 → ClosedLocked */}
                <div className="space-y-2">
                  <p className="text-sm font-medium">{t('finalReport.close.label')}</p>
                  {canAdvanceClose ? (
                    <>
                      <Button
                        type="button"
                        variant="destructive"
                        data-testid="case-close"
                        disabled={
                          closeMutation.isPending ||
                          closeBlockedByVariance ||
                          closeBlockedByConfirm
                        }
                        onClick={() => closeStepUp.request((code) => closeMutation.mutate(code))}
                      >
                        {closeMutation.isPending
                          ? t('finalReport.close.running')
                          : t('finalReport.close.button')}
                      </Button>
                      {closeBlockedByVariance ? (
                        <p className="text-sm text-destructive">
                          {t('finalReport.close.blockedByVariance')}
                        </p>
                      ) : closeBlockedByConfirm ? (
                        <p className="text-sm text-destructive">
                          {t('finalReport.close.blockedByConfirm')}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {t('finalReport.close.hint')}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('finalReport.close.notReady')}
                    </p>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : null}
      {closeStepUp.dialog}
    </div>
  )
}

function SnapshotStat({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="rounded-md border border-border p-4">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-base font-semibold">{value}</dd>
    </div>
  )
}
